// Email Templates — operator-editable templates fired by booking lifecycle events.
// Each template has a trigger (the event it responds to) — operators don't create
// new triggers, only edit the content of existing ones.
//
// The template stores subject + body (with {{variable}} placeholders).
// Use renderEmail(template, bookingData) to swap variables for sending.

export const EMAIL_TRIGGERS = [
  {
    id: 'booking_confirmed_paid',
    name: 'Booking confirmed — Paid online (Stripe)',
    desc: 'Sent right after Stripe charge succeeds. Receipt + ID + agreement.',
    group: 'Booking',
    icon: '✅',
    attachments: ['agreement_pdf', 'receipt_pdf'],
  },
  {
    id: 'booking_confirmed_cash',
    name: 'Booking confirmed — Cash on pickup',
    desc: 'Sent right after booking. Includes pickup location & cash instructions.',
    group: 'Booking',
    icon: '💵',
    attachments: ['agreement_pdf'],
  },
  {
    id: 'booking_confirmed_bank',
    name: 'Booking confirmed — Bank transfer pending',
    desc: 'Sent right after booking. Includes IBAN, amount, reference. Booking held until payment received.',
    group: 'Booking',
    icon: '🏦',
    attachments: ['agreement_pdf', 'proforma_invoice_pdf'],
  },
  {
    id: 'booking_pending_review',
    name: 'Booking received — Awaiting confirmation',
    desc: 'Sent when booking requires operator review (manual confirmation mode).',
    group: 'Booking',
    icon: '⏳',
    attachments: [],
  },
  {
    id: 'booking_approved',
    name: 'Booking approved by operator',
    desc: 'Sent when operator confirms a previously-pending booking.',
    group: 'Booking',
    icon: '👍',
    attachments: ['agreement_pdf'],
  },
  {
    id: 'booking_rejected',
    name: 'Booking declined',
    desc: 'Sent when operator declines a booking. Customer is notified.',
    group: 'Booking',
    icon: '❌',
    attachments: [],
  },
  {
    id: 'payment_received',
    name: 'Payment received — Bank transfer cleared',
    desc: 'Sent when bank transfer is manually marked as received by operator.',
    group: 'Payments',
    icon: '💰',
    attachments: ['receipt_pdf'],
  },
  {
    id: 'agreement_signed',
    name: 'Signed agreement copy',
    desc: 'Sent right after customer e-signs the agreement.',
    group: 'Agreement',
    icon: '✍️',
    attachments: ['agreement_pdf'],
  },
  {
    id: 'pickup_reminder',
    name: 'Pickup reminder — 24h before',
    desc: 'Sent the day before pickup. Includes time, location, what to bring.',
    group: 'Reminders',
    icon: '⏰',
    attachments: [],
  },
  {
    id: 'return_reminder',
    name: 'Return reminder — Day of return',
    desc: 'Sent on the morning of return. Includes return time and location.',
    group: 'Reminders',
    icon: '🔔',
    attachments: [],
  },
  {
    id: 'overdue_notice',
    name: 'Overdue notice',
    desc: 'Sent when customer is past return date. Day-rate continues to apply.',
    group: 'Reminders',
    icon: '⚠️',
    attachments: [],
  },
  {
    id: 'booking_cancelled',
    name: 'Booking cancelled',
    desc: 'Sent when a booking is cancelled (by customer or operator).',
    group: 'Booking',
    icon: '🚫',
    attachments: [],
  },
  {
    id: 'damage_reported',
    name: 'Damage report — Charge to customer',
    desc: 'Sent when a damage is billed to the customer post-rental.',
    group: 'Damages',
    icon: '🔧',
    attachments: ['receipt_pdf'],
  },
];

// Variables that can be inserted into any template.
// Grouped for the side-panel UI in the editor.
export const EMAIL_VARIABLES = {
  Customer: [
    { token: '{{customer_name}}',    desc: 'Full name (first + last)' },
    { token: '{{customer_first}}',   desc: 'First name only' },
    { token: '{{customer_email}}',   desc: 'Email address' },
    { token: '{{customer_phone}}',   desc: 'Phone number' },
    { token: '{{customer_country}}', desc: 'Country' },
  ],
  Booking: [
    { token: '{{booking_ref}}',      desc: 'Public booking reference (e.g. BK-6378)' },
    { token: '{{contract_id}}',      desc: 'Internal contract ID (e.g. CNT-007)' },
    { token: '{{pickup_date}}',      desc: 'Pickup date (e.g. 24 May 2026)' },
    { token: '{{pickup_time}}',      desc: 'Pickup time (e.g. 10:00)' },
    { token: '{{return_date}}',      desc: 'Return date' },
    { token: '{{return_time}}',      desc: 'Return time' },
    { token: '{{pickup_location}}',  desc: 'Pickup location name' },
    { token: '{{return_location}}',  desc: 'Return location name' },
    { token: '{{rental_days}}',      desc: 'Number of days' },
  ],
  Vehicle: [
    { token: '{{vehicle_model}}',    desc: 'Model name (e.g. Toyota Corolla)' },
    { token: '{{vehicle_plate}}',    desc: 'Plate — only use post-pickup / on agreement' },
    { token: '{{vehicle_icon}}',     desc: 'Vehicle icon emoji' },
  ],
  Money: [
    { token: '{{total}}',            desc: 'Total amount with currency (e.g. €450)' },
    { token: '{{deposit}}',          desc: 'Deposit amount' },
    { token: '{{paid}}',             desc: 'Already paid amount' },
    { token: '{{amount_due}}',       desc: 'Amount still owed' },
    { token: '{{payment_method}}',   desc: 'Cash / Card / Bank Transfer' },
  ],
  'Bank transfer': [
    { token: '{{bank_name}}',        desc: 'Your bank name' },
    { token: '{{bank_account}}',     desc: 'Account holder name' },
    { token: '{{iban}}',             desc: 'IBAN number' },
    { token: '{{swift}}',            desc: 'SWIFT / BIC code' },
    { token: '{{payment_reference}}', desc: 'Use this as the transfer reference' },
  ],
  Company: [
    { token: '{{company_name}}',     desc: 'Your company name' },
    { token: '{{company_logo}}',     desc: 'Your logo (optional — auto-shown at top of every email)' },
    { token: '{{company_phone}}',    desc: 'Your phone' },
    { token: '{{company_email}}',    desc: 'Your email' },
    { token: '{{company_address}}',  desc: 'Your address' },
    { token: '{{company_website}}',  desc: 'Your website' },
  ],
  Links: [
    { token: '{{manage_booking_link}}', desc: 'Link to manage / cancel booking' },
    { token: '{{agreement_link}}',      desc: 'Direct link to signed agreement PDF' },
    { token: '{{receipt_link}}',        desc: 'Direct link to receipt / pro forma' },
  ],
  Damages: [
    { token: '{{damage_type}}',     desc: 'Type (Scratch, Dent, etc.)' },
    { token: '{{damage_location}}', desc: 'Where on vehicle' },
    { token: '{{damage_cost}}',     desc: 'Charge amount' },
    { token: '{{damage_description}}', desc: 'Description of damage' },
  ],
};

// Default content for each trigger — operators can fully overwrite.
// Plain text with {{variable}} placeholders. Newlines preserved.
const DEFAULTS = {
  booking_confirmed_paid: {
    subject: '✅ Your {{vehicle_model}} is booked — Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Your booking with {{company_name}} is confirmed.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}
📅 Return:  {{return_date}} {{return_time}} at {{return_location}}
👥 {{rental_days}} day(s)

💳 Paid online — €{{total}}
✓ No further payment needed at pickup

Please bring:
• Your driver's licence
• A credit card (for the security deposit hold of €{{deposit}})
• Your passport / ID

Booking reference: {{booking_ref}}
Manage your booking: {{manage_booking_link}}

See you soon!
{{company_name}}
{{company_phone}}
{{company_email}}`,
  },

  booking_confirmed_cash: {
    subject: '💵 Booking confirmed — Pay at pickup · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Your booking is confirmed. You chose to pay cash at pickup.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}
📅 Return:  {{return_date}} {{return_time}}
👥 {{rental_days}} day(s)

💵 Total to pay at pickup: €{{total}}
🔒 Security deposit (refundable): €{{deposit}}

Please bring:
• Your driver's licence
• Your passport / ID
• Cash for €{{total}} + €{{deposit}}

If you'd prefer to pay by card on arrival, that's also fine — let us know.

Booking reference: {{booking_ref}}
Manage your booking: {{manage_booking_link}}

See you on {{pickup_date}}!
{{company_name}}
{{company_phone}}`,
  },

  booking_confirmed_bank: {
    subject: '🏦 Booking received — Bank transfer required · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Thank you for booking with {{company_name}}!

Your reservation is held while we wait for your bank transfer.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}
📅 Return:  {{return_date}} {{return_time}}
👥 {{rental_days}} day(s)

💰 Amount due:  €{{total}}

━━━ BANK DETAILS ━━━
Bank:       {{bank_name}}
Account:    {{bank_account}}
IBAN:       {{iban}}
SWIFT/BIC:  {{swift}}
Reference:  {{payment_reference}}

⚠️ Important: please use "{{payment_reference}}" as the transfer reference,
otherwise we cannot match the payment to your booking.

We'll send you a final confirmation once payment is received. If we don't
see the transfer 48 hours before pickup, the booking will be cancelled.

Booking reference: {{booking_ref}}
Manage your booking: {{manage_booking_link}}

Questions? Reply to this email.
{{company_name}}
{{company_phone}}`,
  },

  booking_pending_review: {
    subject: '⏳ Booking received — Awaiting confirmation · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

We've received your booking request and our team is reviewing it.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}
📅 Return:  {{return_date}} {{return_time}}
💰 Total:   €{{total}}

You'll receive a confirmation email within 24 hours.
No charge has been made yet.

Booking reference: {{booking_ref}}

{{company_name}}
{{company_phone}}`,
  },

  booking_approved: {
    subject: '👍 Your booking is confirmed! · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Great news — your booking has been approved.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}
📅 Return:  {{return_date}} {{return_time}}
💰 Total:   €{{total}} ({{payment_method}})

Please bring your driver's licence, ID, and the payment card used.

Booking reference: {{booking_ref}}
Manage your booking: {{manage_booking_link}}

See you on {{pickup_date}}!
{{company_name}}`,
  },

  booking_rejected: {
    subject: '❌ Booking could not be confirmed · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Unfortunately we're unable to confirm your booking at this time.

If you paid online, a full refund will be processed within 3-5 business days.
If you have any questions, please reply to this email or call us at
{{company_phone}}.

We'd love to help you find another option — feel free to browse our other
vehicles or pick different dates.

Sorry for the inconvenience.
{{company_name}}`,
  },

  payment_received: {
    subject: '💰 Payment received · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

We've received your bank transfer of €{{paid}} for booking {{booking_ref}}.

Your booking is now fully confirmed.

🚗 Vehicle: {{vehicle_model}}
📅 Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}

Receipt attached. See you on {{pickup_date}}!

{{company_name}}
{{company_phone}}`,
  },

  agreement_signed: {
    subject: '✍️ Your signed rental agreement · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Attached is a copy of your signed rental agreement for your records.

Vehicle: {{vehicle_model}}
Pickup:  {{pickup_date}} {{pickup_time}} at {{pickup_location}}

Keep this for your records — you may need it at pickup.

{{company_name}}`,
  },

  pickup_reminder: {
    subject: '⏰ Pickup tomorrow — {{vehicle_model}} · {{pickup_time}}',
    body:
`Hi {{customer_first}},

Quick reminder: your rental pickup is tomorrow.

🚗 Vehicle: {{vehicle_model}}
📅 Tomorrow at {{pickup_time}}
📍 {{pickup_location}}

Please bring:
• Driver's licence
• Passport / ID
• Payment card

If you need to change anything: {{manage_booking_link}}

See you tomorrow!
{{company_name}}
{{company_phone}}`,
  },

  return_reminder: {
    subject: '🔔 Return today — {{return_time}} at {{return_location}}',
    body:
`Hi {{customer_first}},

Friendly reminder: your rental is due back today.

🚗 Vehicle: {{vehicle_model}}
📅 Today at {{return_time}}
📍 {{return_location}}

Please fuel the car as agreed and clean any obvious mess before return.

Need to extend? Reply to this email or call us at {{company_phone}}.

Thanks!
{{company_name}}`,
  },

  overdue_notice: {
    subject: '⚠️ Your rental is overdue · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Your rental ({{vehicle_model}}) was due back on {{return_date}} at {{return_time}}
and has not yet been returned.

The daily rate continues to apply until the vehicle is returned.

Please contact us immediately at {{company_phone}} or reply to this email.

{{company_name}}`,
  },

  booking_cancelled: {
    subject: '🚫 Booking cancelled · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Your booking {{booking_ref}} has been cancelled.

If a payment was made, a refund will be processed within 3-5 business days
(minus any applicable cancellation fees).

We hope to see you again soon.

{{company_name}}
{{company_phone}}`,
  },

  damage_reported: {
    subject: '🔧 Damage charge — {{vehicle_model}} · Ref {{booking_ref}}',
    body:
`Hi {{customer_first}},

Following the return of {{vehicle_model}} on {{return_date}}, we found
damage that wasn't there at pickup.

Damage:      {{damage_type}}
Location:    {{damage_location}}
Description: {{damage_description}}
Charge:      €{{damage_cost}}

Photos and a pro forma are attached. The amount has been charged to your
card / will be deducted from your deposit.

If you'd like to dispute this charge, please reply to this email within
14 days with any supporting information.

{{company_name}}
{{company_phone}}`,
  },
};

// Build initial templates array from defaults
export const initialEmailTemplates = EMAIL_TRIGGERS.map(t => ({
  id: 'tmpl-' + t.id,
  trigger: t.id,
  name: t.name,
  enabled: true,
  subject: DEFAULTS[t.id]?.subject || '',
  body:    DEFAULTS[t.id]?.body    || '',
  updatedAt: null,
}));
